import { memo, useMemo } from "react";
import { StyleSheet } from "react-native-unistyles";
import { View, Text } from "@src/components";
import SegmentedSelector from "@src/components/SegmentedSelector";
import { toSentenceCase } from "@visioni/a3s-ui/utils";
import type { UserWithSites } from "@src/features/settings/data/mock-all-users";
import { isWeb } from "@src/utils/web-helpers";

export type AssignRole = "manager" | "viewer";

interface AssignRolesUserListProps {
  users: UserWithSites[];
  roleMap: Map<string, AssignRole>;
  onRoleChange: (userId: string, role: AssignRole) => void;
}

export const AssignRolesUserList = memo(({ users, roleMap, onRoleChange }: AssignRolesUserListProps) => {
  const options = useMemo(() => [{ title: "Manager" }, { title: "Viewer" }], []);

  return (
    <View style={styles.card}>
      {users.map((user, index) => {
        const isLast = index === users.length - 1;
        const selectedRole = roleMap.get(user.id) ?? "viewer";

        return (
          <View
            key={user.id}
            bg="surfaceCard"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            style={[styles.userRow, !isLast && styles.userRowBorder]}
          >
            <View gap="xs" flex={1}>
              <Text textStyle="sm/regular">{user.name}</Text>
              {user.siteNames.length > 0 && (
                <Text textStyle="xxs/regular" color="textMuted">
                  {user.siteNames.join(", ")}
                </Text>
              )}
              {user.email && (
                <Text textStyle="xxs/regular" color="textMuted">
                  {user.email}
                </Text>
              )}
              {user.phoneNumber && (
                <Text textStyle="xxs/regular" color="textMuted">
                  {user.phoneNumber}
                </Text>
              )}
            </View>
            <View>
              <SegmentedSelector
                optionsLabels={options}
                selectedOption={toSentenceCase(selectedRole)}
                onSelectOption={(option) => onRoleChange(user.id, option.toLowerCase() as AssignRole)}
                containerStyle={styles.roleSelectorContainer}
                activeBoxStyle={styles.roleSelectorActiveBox}
                width={isWeb ? 200 : undefined}
              />
            </View>
          </View>
        );
      })}
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  card: {
    borderRadius: theme.spacing.lg,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    overflow: "hidden",
  },
  userRow: {
    paddingVertical: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing["3xl"],
    gap: theme.spacing.xxs,
  },
  userRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderCard,
  },
  roleSelectorContainer: {
    backgroundColor: "transparent",
  },
  roleSelectorActiveBox: {
    backgroundColor: theme.colors.surface2.hover,
  },
}));
